import {Component, EventEmitter, Input, Output} from '@angular/core';
import {Product} from '@models/product.interface';
import {FontAwesomeService} from '@services/font-awesome.service';
import {TranslateService} from '@ngx-translate/core';
import {DateFormatPipe} from '@pipes/date-format.pipe';

@Component({
  selector: 'app-search-result-card',
  templateUrl: './search-result-card.component.html',
  styleUrls: ['./search-result-card.component.scss']
})
export class SearchResultCardComponent {
  @Input() failure: boolean;
  @Input() fullHeight: boolean;
  @Input() isLoading: boolean;
  @Input() product: Product;
  @Input() searchParams: any;
  @Input() isActive: boolean;
  @Input() searchResultId: string;
  @Input() cheapest: boolean;
  @Input() fastest: boolean;

  @Output() toggleActive: EventEmitter<string> = new EventEmitter<string>();

  constructor(
    public faService: FontAwesomeService,
    private translate: TranslateService
  ) { }

  handleClick() {
    this.toggleActive.emit( this.buildUniqueId(this.searchResultId, this.product.product_id));
  }

  buildUniqueId(searchResultUuid: string, productId: string | number): string {
    return `${searchResultUuid}-${productId}`;
  }

  get timeUntilDelivery(): string {
    let timUntilDelivery: string;
    const time = this.product.time_until_delivery;
    const day = this.translate.instant('common.date.day');
    const dayPlural = this.translate.instant('common.date.dayPlural');
    const hour = this.translate.instant('common.date.hour');
    const hourPlural = this.translate.instant('common.date.hourPlural');
    if (time < 24) {
      timUntilDelivery = `${time} ${time > 1 ? hourPlural : hour}`;
    } else {
      const hours: number = time % 24;
      const days: number = (time - hours) / 24;
      timUntilDelivery = `${days} ${days > 1 ? dayPlural : day} ${hours} ${hours > 1 ? hourPlural : hour}`;
    }
    return timUntilDelivery;
  }

  /*
  public get pickupDate(): string {
    if (this.product.product_code === 'DHLParti') {
      return this.translate.instant('common.date.parti.pickup');
    } else {
      const {startTime, endTime} = this.getTimespan(this.product.pickup_time_from, this.product.pickup_time_to);
      const timeString =  this.translate.instant('common.date.timeLabel') + ' ' + startTime + '-' + endTime;

      return `${(new DateFormatPipe(this.translate)).transform(
        this.product.pickup_date, 'mediumDate', '', this.translate.currentLang)}, ${timeString}`;
    }
  }
  */

  get priceWithRequiredAdditionalServices(): number {
    const { serviceAmount } = this.calculateRequiredAdditionalServicesAmount();

    return this.product.price + serviceAmount;
  }

  calculateRequiredAdditionalServicesAmount(): { serviceAmount: number; vatAmount: number } {
    if (!this.product.additional_services) {
      return { serviceAmount: 0, vatAmount: 0 };
    }

    return Object.values(this.product.additional_services).reduce((acc, service: any) => {
      if (service.required && service.active && !service.disabled) {
        const amount = parseFloat(service.price.amount);
        const vatRate = service.price.vat_rate || 0;
        acc.serviceAmount += amount;
        acc.vatAmount += amount * (vatRate / 100);
      }
      return acc;
    }, { serviceAmount: 0, vatAmount: 0 });
  }

  public get pickupDate(): string {
    if (this.product.product_code === 'DHLParti') {
      return this.translate.instant('common.date.parti.pickup');
    } else {
      return `${(new DateFormatPipe(this.translate)).transform(
        this.product.pickup_date, 'dd MMM', '', this.translate.currentLang)}`;
    }
  }

  public get pickupTime(): string {
      const {startTime, endTime} = this.getTimespan(this.product.pickup_time_from, this.product.pickup_time_to);

      return this.translate.instant('common.date.timeLabel') + ' ' + startTime + '-' + endTime;
  }

  /*
  public get deliveryDate(): string {
    if (this.product.product_code === 'DHLParti') {
      return this.translate.instant('common.date.parti.delivery');
    } else {
      const {startTime, endTime} = this.getTimespan(this.product.delivery_time_from, this.product.delivery_time_to);
      const timeString = this.translate.instant('common.date.timeLabel') + ' ' + startTime + '-' + endTime;

      return `${(new DateFormatPipe(this.translate)).transform(
        this.product.delivery_date, 'mediumDate', '', this.translate.currentLang)}, ${timeString}`;
    }
  }
   */

  public get deliveryDate(): string {
    if (this.product.product_code === 'DHLParti') {
      return this.translate.instant('common.date.parti.delivery');
    } else {
      return `${(new DateFormatPipe(this.translate)).transform(
        this.product.delivery_date, 'dd MMM', '', this.translate.currentLang)}`;
    }
  }

  public get deliveryTime(): string {
      const {startTime, endTime} = this.getTimespan(this.product.delivery_time_from, this.product.delivery_time_to);
      return this.translate.instant('common.date.timeLabel') + ' ' + startTime + '-' + endTime;
  }

  private getTimespan(from, to) {
    const startTime = from.length === 8 ? from.slice(0, -3) : from;
    const endTime = to.length === 8 ? to.slice(0, -3) : to;

    return {startTime, endTime};
  }

  public isGuaranteed(): boolean {
    return this.product.is_guaranteed;
    /*
    switch (this.product.name) {
      case 'UPS':
        return this.product.is_guaranteed;
      case 'DHL Express':
        switch (this.product.product_code) {
          case 'I' || 'K' || '1' || 'T' :
            console.log(this.product.name + ', ' + this.product.product_name + ', ' + this.product.product_code);
            return true;
          default:
            return false;
        }
      default :
        return false;
    }
    */
  }

  public get deliveryTimeInfo(): object {
    const info = {
      className: 'default',
      text: this.translate.instant('account.section.createConsignment.searchResult.notGuaranteed'),
      icon: this.faService.faClock
    };

    if (this.product.is_guaranteed) {
      info.className = 'guaranteed';
      info.text = this.translate.instant('account.section.createConsignment.searchResult.guaranteed') + ' ' + this.product.delivery_time_to;
      info.icon = this.faService.faClock;
    } else if (this.product.product_code === 'DHLParti') {
      info.className = 'unsure';
      info.text = this.translate.instant('account.section.createConsignment.searchResult.unsure');
      info.icon = this.faService.faClock;
    }

    return info;
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/consignment/search-results/components/search-result-card/search-result-card.component.ts